import React, { useEffect, useState } from 'react';
import { X, Loader2, Download } from 'lucide-react';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import {
  downloadTicketAttachment,
  fetchTicketAttachmentPreviewBlob,
} from '../../../utils/crmTicketDownload';
import { useToast } from '../../../hooks/use-toast';
import { isMobileContainer } from '../../../utils/device';

export default function CrmTicketAttachmentPreview({ attachment, onClose }) {
  const { toast } = useToast();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPdf = attachment?.mime_type === 'application/pdf';
  const isImage = attachment?.mime_type?.startsWith('image/');
  const name = attachment?.original_name || 'attachment';
  const isMobile = isMobileContainer();

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      setLoading(true);
      try {
        const blob = await fetchTicketAttachmentPreviewBlob(attachment.id);
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        setUrl(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.id]);

  const handleDownload = async () => {
    try {
      await downloadTicketAttachment(attachment.id, name);
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not download attachment',
        variant: 'destructive',
      });
    }
  };

  if (isImage) {
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          overlayClassName="z-[80]"
          className={`z-[80] p-0 border-0 bg-black/95 overflow-hidden ${
            isMobile ? 'max-w-[100vw] w-full h-[100dvh] rounded-none' : 'max-w-4xl'
          }`}
        >
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
            <button
              type="button"
              onClick={handleDownload}
              className="h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Download"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center justify-center min-h-[50vh] h-full p-4 animate-in zoom-in-95 fade-in duration-200">
            {loading && <Loader2 className="w-8 h-8 animate-spin text-white/70" />}
            {!loading && url && (
              <img
                src={url}
                alt={name}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            )}
            {!loading && !url && (
              <p className="text-white/70 text-sm">Preview not available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <span className="text-sm font-medium truncate">{name}</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handleDownload} title="Download">
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px] bg-muted/30 p-4">
          {loading && <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
          {!loading && url && isPdf && (
            <iframe src={url} title={name} className="w-full h-[70vh] border-0" />
          )}
          {!loading && (!url || !isPdf) && (
            <p className="text-muted-foreground">Preview not available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
